import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

function distM(lat1: number, lng1: number, lat2: number, lng2: number) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

function StatusBadge({ status, type }: any) {
  const map: any = {
    late: ['迟到', 'bg-orange-100 text-orange-700'],
    early: ['早退', 'bg-purple-100 text-purple-700'],
    normal: ['正常', 'bg-green-100 text-green-700'],
  };
  const [txt, cls] = map[status] || ['—', 'bg-slate-100 text-slate-500'];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>
      {type === 'outwork' ? '外勤 · ' : ''}
      {txt}
    </span>
  );
}

export default function Attendance() {
  const nav = useNavigate();
  const [today, setToday] = useState<any>(null);
  const [report, setReport] = useState<any>(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loc, setLoc] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [perms, setPerms] = useState<string[]>([]);

  const load = () => {
    api('/attendance/today').then(setToday).catch(() => {});
    api(`/attendance/report?month=${month}`).then(setReport).catch(() => {});
  };
  useEffect(() => {
    load();
    api('/auth/me').then((d) => setPerms(d.permissions || [])).catch(() => {});
    // 自动尝试定位，显示是否在考勤范围
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setLoc({ lat: p.coords.latitude, lng: p.coords.longitude, ok: true }),
        () => setLoc({ ok: false }),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, []);

  useEffect(() => {
    api(`/attendance/report?month=${month}`).then(setReport).catch(() => {});
  }, [month]);

  const rec = today?.record;
  const group = today?.group;
  const inRange =
    loc?.lat != null && group?.center_lat != null
      ? distM(loc.lat, loc.lng, group.center_lat, group.center_lng)! <= (group.radius || 300)
      : null;

  const punch = async (forceOutwork = false) => {
    setBusy(true);
    setResult(null);
    let lat: number | null = null;
    let lng: number | null = null;
    let address = '未定位';
    if (navigator.geolocation) {
      try {
        const p: any = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000 })
        );
        lat = p.coords.latitude;
        lng = p.coords.longitude;
        address = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      } catch {
        address = '定位失败';
      }
    }
    try {
      const r = await api('/attendance/punch', {
        method: 'POST',
        body: JSON.stringify({ lat, lng, address, type: forceOutwork ? 'outwork' : undefined }),
      });
      setResult(r);
      load();
    } catch (e: any) {
      setResult({ error: e.message });
    } finally {
      setBusy(false);
    }
  };

  const btnLabel = !rec ? '上 班 打 卡' : !rec.check_out ? '下 班 打 卡' : '今日已完成';

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">考勤打卡</h2>
        {perms.includes('attendance:edit') && (
          <button onClick={() => nav('/attendance-groups')} className="text-xs text-brand-600 border border-brand-200 rounded px-2 py-1">
            ⚙ 考勤组
          </button>
        )}
      </div>

      {/* 定位状态 */}
      <div className="bg-white rounded-xl shadow p-3 text-sm flex items-center justify-between">
        <span className="text-slate-500">当前位置</span>
        {loc == null && <span className="text-slate-400">定位中…</span>}
        {loc && loc.ok === false && <span className="text-amber-600">定位失败（可继续打卡，记为外勤）</span>}
        {inRange === true && <span className="text-green-600 font-medium">✓ 在考勤范围内</span>}
        {inRange === false && <span className="text-orange-600 font-medium">⚠ 不在考勤范围（将记为外勤）</span>}
        {inRange === null && loc?.ok && <span className="text-slate-400">该考勤组不限位置</span>}
      </div>

      {/* 打卡大按钮 */}
      <div className="bg-white rounded-2xl shadow p-6 text-center">
        <div className="text-sm text-slate-500 mb-1">{group?.work_start} - {group?.work_end}</div>
        <button
          disabled={busy || (rec && rec.check_out)}
          onClick={() => punch(false)}
          className="w-44 h-44 mx-auto rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white text-xl font-bold shadow-lg active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center"
        >
          <span className="text-2xl">{!rec ? '🟢' : !rec.check_out ? '🟠' : '✅'}</span>
          <span className="mt-2">{busy ? '打卡中…' : btnLabel}</span>
        </button>
        {(!rec || !rec.check_out) && (
          <button onClick={() => punch(true)} disabled={busy} className="mt-3 text-xs text-orange-600 underline">
            或：外勤打卡（脱离办公地点）
          </button>
        )}
      </div>

      {/* 打卡结果 */}
      {result && !result.error && (
        <div className="rounded-xl p-4 text-center font-medium bg-green-100 text-green-700 animate-pulse">
          {result.message} · {result.time}
        </div>
      )}
      {result?.error && <div className="rounded-xl p-4 text-center font-medium bg-red-100 text-red-700">{result.error}</div>}

      {/* 今日记录 */}
      {rec && (
        <div className="bg-white rounded-xl shadow p-4 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">上班</span><span className="text-green-600">{rec.check_in?.split(' ')[1] || '—'}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">下班</span><span className="text-orange-600">{rec.check_out?.split(' ')[1] || '未签退'}</span></div>
          <div className="flex justify-between text-sm items-center"><span className="text-slate-500">状态</span><StatusBadge status={rec.status} type={rec.type} /></div>
          {rec.address && <div className="text-xs text-slate-400">📍 {rec.address}</div>}
        </div>
      )}

      {/* 本月报表 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="p-3 bg-slate-50 border-b font-medium text-sm text-slate-600 flex items-center justify-between">
          <span>本月考勤统计</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border rounded px-2 py-1 text-xs" />
        </div>
        {report && (
          <div className="p-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-2xl font-bold text-blue-600">{report.summary.present}</div><div className="text-xs text-slate-400">出勤</div></div>
              <div><div className="text-2xl font-bold text-orange-600">{report.summary.late}</div><div className="text-xs text-slate-400">迟到</div></div>
              <div><div className="text-2xl font-bold text-purple-600">{report.summary.early}</div><div className="text-xs text-slate-400">早退</div></div>
              <div><div className="text-2xl font-bold text-red-500">{report.summary.absentDays}</div><div className="text-xs text-slate-400">缺卡</div></div>
              <div><div className="text-2xl font-bold text-teal-600">{report.summary.outwork}</div><div className="text-xs text-slate-400">外勤</div></div>
              <div><div className="text-2xl font-bold text-slate-500">{report.summary.workdays}</div><div className="text-xs text-slate-400">工作日</div></div>
            </div>
            {/* 明细 */}
            <div className="mt-3 max-h-48 overflow-y-auto space-y-1">
              {report.detail.map((d: any) => (
                <div key={d.date} className="flex justify-between text-xs border-b border-slate-50 py-1">
                  <span className="text-slate-500">{d.date.slice(5)}</span>
                  <span className="text-slate-400">{d.check_in?.split(' ')[1] || '—'} ~ {d.check_out?.split(' ')[1] || '—'}</span>
                  <StatusBadge status={d.late ? 'late' : d.early ? 'early' : 'normal'} type={d.outwork ? 'outwork' : 'normal'} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
